import { ChartContainer, Flex, Text } from "@suns/design-system";
import { ApolloGrade } from "@suns/api";
import {
  Bar,
  BarChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Cell,
  LabelList,
} from "recharts";
import { apolloGradeColors } from "@/shared/utils/colors";
import {
  ApolloGradeValueLabels,
  ApolloGradeValueShortLabels,
} from "@/pages/reports/reports-const";
// eslint-disable-next-line no-restricted-imports -- DO NOT COPY THIS LINE. See docs/lints.md.
import { PlayerMetadataRow } from "@suns/api/generated-client/apollo";
import { padDecimals } from "@/shared/utils/helper-functions";

export function ApolloGradeDistributionChart({
  playerMetadata,
  size = "md",
}: {
  playerMetadata?: PlayerMetadataRow;
  size?: "sm" | "md";
}) {
  const probabilityData = {
    PROB_MINOR_LEAGUE: playerMetadata?.probabilityMinorLeague,
    PROB_ROSTER: playerMetadata?.probabilityRoster,
    PROB_ROTATION: playerMetadata?.probabilityRotation,
    PROB_TOP_ROTATION: playerMetadata?.probabilityTopRotation,
    PROB_STARTER: playerMetadata?.probabilityStarter,
    PROB_ALL_STAR: playerMetadata?.probabilityAllStar,
    PROB_ALL_STAR_PLUS: playerMetadata?.probabilityAllStarPlus,
    PROB_FRANCHISE: playerMetadata?.probabilityFranchise,
  };

  const chartConfig: Record<string, { label: string; color: string }> = {
    PROB_MINOR_LEAGUE: {
      label: ApolloGrade.MINOR_LEAGUE,
      color: apolloGradeColors[ApolloGrade.MINOR_LEAGUE].hex,
    },
    PROB_ROSTER: {
      label: ApolloGrade.ROSTER,
      color: apolloGradeColors[ApolloGrade.ROSTER].hex,
    },
    PROB_ROTATION: {
      label: ApolloGrade.ROTATION,
      color: apolloGradeColors[ApolloGrade.ROTATION].hex,
    },
    PROB_TOP_ROTATION: {
      label: ApolloGrade.TOP_ROTATION,
      color: apolloGradeColors[ApolloGrade.TOP_ROTATION].hex,
    },
    PROB_STARTER: {
      label: ApolloGrade.STARTER,
      color: apolloGradeColors[ApolloGrade.STARTER].hex,
    },
    PROB_ALL_STAR: {
      label: ApolloGrade.ALL_STAR,
      color: apolloGradeColors[ApolloGrade.ALL_STAR].hex,
    },
    PROB_ALL_STAR_PLUS: {
      label: ApolloGrade.ALL_STAR_PLUS,
      color: apolloGradeColors[ApolloGrade.ALL_STAR_PLUS].hex,
    },
    PROB_FRANCHISE: {
      label: ApolloGrade.FRANCHISE,
      color: apolloGradeColors[ApolloGrade.FRANCHISE].hex,
    },
  };

  const formattedData: {
    label: string | null;
    value: number | null;
    fill?: string;
  }[] = Object.entries(probabilityData).map(([key, value]) => ({
    label: chartConfig[key]?.label as string,
    value: value ? value * 100 : 0,
    fill: chartConfig[key]?.color,
  }));

  const hasData = formattedData.some(
    (entry) => entry.value !== null && entry.value > 0
  );

  if (!hasData) {
    return null;
  }

  return (
    <Flex direction="down">
      <Text heading muted size="md" className="mb-4">
        Apollo Grade Analytic Distribution
      </Text>
      <ChartContainer config={chartConfig} overflow={true} className="pt-1">
        <BarChart data={formattedData}>
          <CartesianGrid stroke="#eee" vertical={false} />
          <XAxis
            dataKey="label"
            interval={0}
            tickFormatter={(value) => {
              const typedValue =
                value as keyof typeof ApolloGradeValueShortLabels;
              if (size == "md") return ApolloGradeValueShortLabels[typedValue];
              return ApolloGradeValueLabels[typedValue];
            }}
          />
          <YAxis
            width={35}
            interval={0}
            domain={[0, 100]}
            tickFormatter={(value) => `${value}%`}
            tickLine={false}
          />
          <Bar dataKey="value" isAnimationActive={false} radius={4}>
            <LabelList
              position="top"
              offset={5}
              className="fill-foreground"
              fontSize={12}
              formatter={(value: number) => `${padDecimals(value)}%`}
            />
            {formattedData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.fill} />
            ))}
          </Bar>
        </BarChart>
      </ChartContainer>
    </Flex>
  );
}
